import { useState } from "react";

import { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { DocumentGroup, documentsApi } from "@/api";
import { useTranslation } from "@/translations";

import { DragData, PendingDocumentMove, isAssetInventoryZone, isDropAllowed } from "./DropZoneDnd.types";
import { invalidateDocumentZoneQueries } from "./DropZoneDnd.utils";

export function useDocumentDnd(
  clientId: string | undefined,
  tryAddPendingMove: (move: PendingDocumentMove) => boolean
) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlertContext();
  const [activeDrag, setActiveDrag] = useState<DragData | null>(null);

  const moveDocumentMutation = useMutation({
    mutationFn: async (params: { target: DocumentGroup; fileId: string }) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      await documentsApi.moveDocument({
        clientId,
        moveDocumentRequest: params,
      });
    },
    onSuccess: () => {
      invalidateDocumentZoneQueries(queryClient, clientId);
      showSuccess(t("dropzone.dnd.moveSuccess"));
    },
    onError: () => showError(t("dropzone.dnd.moveError")),
  });

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as DragData | undefined;
    if (data) {
      setActiveDrag(data);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDrag(null);

    const { active, over } = event;
    if (!over) {
      return;
    }

    const dragData = active.data.current as DragData | undefined;
    if (!dragData) {
      return;
    }

    const targetZone = over.id as DocumentGroup;
    if (!isDropAllowed(dragData.sourceZone, targetZone)) {
      return;
    }

    if (isAssetInventoryZone(dragData.sourceZone) || isAssetInventoryZone(targetZone)) {
      // Asset inventory moves are pending form changes, persisted on the owning view's save.
      // No handler can only happen in an unmount race — dropping the event is the safe outcome.
      tryAddPendingMove({ document: dragData.file, source: dragData.sourceZone, target: targetZone });
      return;
    }

    moveDocumentMutation.mutate({
      target: targetZone,
      fileId: dragData.file.documentId,
    });
  };

  const handleDragCancel = () => {
    setActiveDrag(null);
  };

  return {
    activeDrag,
    isMoving: moveDocumentMutation.isPending,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  };
}
